import { Link } from "react-router-dom";
import { testimonials, companyDetails } from "../data";
import SEO from "../components/SEO";

export default function Recommendations() {
  const recommendationsSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Forside",
            "item": "https://lederskab.nu/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Anbefalinger",
            "item": "https://lederskab.nu/anbefalinger"
          }
        ]
      }
    ]
  };

  return (
    <div className="bg-white">
      <SEO
        title="Anbefalinger & Erfaringer | Hvad siger lederne? | Lederskab.nu"
        description="Læs ægte udtalelser og erfaringer fra ledere og virksomheder, der har deltaget i lederforløb hos Anita Lauridsen og Lederskab.nu."
        canonicalPath="/anbefalinger"
        structuredData={recommendationsSchema}
      />
      {/* Header Banner */}
      <section className="relative bg-gradient-to-br from-maersk-dark to-navy-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.15),transparent_70%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-left">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-orange bg-brand-orange/20 border border-brand-orange/30 px-3 py-1.5 rounded-full">
              Anbefalinger & Cases
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-bold mt-4 mb-6 leading-tight">
              Det siger de om Lederskab.nu
            </h1>
            <p className="text-gray-300 text-lg sm:text-xl leading-relaxed">
              Mennesker skaber resultater – når de bliver ledet godt. Læs om de erfaringer og resultater, som ledere, virksomheder og samarbejdspartnere har opnået gennem vores forløb.
            </p>
          </div>
        </div>
      </section>

      {/* Section 1: Lederne siger */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-12 border-l-4 border-brand-orange pl-4">
            <h2 className="font-display text-3xl font-bold text-maersk-dark">
              Lederne siger
            </h2>
            <p className="text-navy-700 mt-2">
              Udtalelser fra ledere, mellemledere og souschefer, der har gennemgået vores 12-måneders lederforløb.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.lederne.map((t, idx) => (
              <div
                key={idx}
                className="bg-maersk-light rounded-2xl p-8 border border-maersk-blue/5 shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between text-left"
              >
                <div>
                  {/* Stars */}
                  <div className="flex gap-1 text-amber-400 mb-5">
                    {[...Array(t.rating)].map((_, i) => (
                      <i key={i} className="fa-solid fa-star text-sm" />
                    ))}
                  </div>
                  <p className="text-navy-700 text-sm leading-relaxed italic mb-6">
                    "{t.quote}"
                  </p>
                </div>
                <div className="border-t border-maersk-blue/10 pt-4">
                  <h4 className="font-bold text-maersk-dark text-sm">{t.author}</h4>
                  <p className="text-xs text-maersk-blue font-medium mt-0.5">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 2: Virksomhederne siger */}
      <section className="py-20 bg-navy-50 border-y border-maersk-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-12 border-l-4 border-maersk-blue pl-4">
            <h2 className="font-display text-3xl font-bold text-maersk-dark">
              Virksomhederne siger
            </h2>
            <p className="text-navy-700 mt-2">
              Erfaringer fra virksomheder, der har sendt flere ledere afsted eller har fået skræddersyet interne forløb.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl">
            {testimonials.virksomhederne.map((t, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-8 border border-maersk-blue/10 shadow-md hover:shadow-lg transition-all duration-300 flex flex-col justify-between text-left"
              >
                <div>
                  {/* Stars */}
                  <div className="flex gap-1 text-amber-400 mb-5">
                    {[...Array(t.rating)].map((_, i) => (
                      <i key={i} className="fa-solid fa-star text-sm" />
                    ))}
                  </div>
                  <p className="text-navy-700 text-sm leading-relaxed italic mb-6">
                    "{t.quote}"
                  </p>
                </div>
                <div className="border-t border-maersk-blue/10 pt-4">
                  <h4 className="font-bold text-maersk-dark text-sm">{t.author}</h4>
                  <p className="text-xs text-maersk-blue font-medium mt-0.5">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Samarbejdspartnerne siger */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-12 border-l-4 border-brand-orange pl-4">
            <h2 className="font-display text-3xl font-bold text-maersk-dark">
              Samarbejdspartnerne siger
            </h2>
            <p className="text-navy-700 mt-2">
              Udtalelser fra eksterne partnere og konsulenter, der sparrer med os omkring ledelsesudvikling og arbejdsmiljø.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl">
            {testimonials.samarbejdspartnerne.map((t, idx) => (
              <div
                key={idx}
                className="bg-maersk-light rounded-2xl p-8 border border-maersk-blue/5 shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between text-left"
              >
                <div>
                  {/* Stars */}
                  <div className="flex gap-1 text-amber-400 mb-5">
                    {[...Array(t.rating)].map((_, i) => (
                      <i key={i} className="fa-solid fa-star text-sm" />
                    ))}
                  </div>
                  <p className="text-navy-700 text-sm leading-relaxed italic mb-6">
                    "{t.quote}"
                  </p>
                </div>
                <div className="border-t border-maersk-blue/10 pt-4">
                  <h4 className="font-bold text-maersk-dark text-sm">{t.author}</h4>
                  <p className="text-xs text-maersk-blue font-medium mt-0.5">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Checklist */}
      <section className="py-20 bg-navy-50 border-t border-maersk-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 sm:p-12 border border-maersk-blue/10 shadow-xl text-left">
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-maersk-dark mb-8 text-center">
              Derfor vælger ledere Lederskab.nu
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-circle-check text-brand-orange text-lg mt-0.5 shrink-0" />
                <span className="text-navy-700 font-medium">Mere ro og overblik</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-circle-check text-brand-orange text-lg mt-0.5 shrink-0" />
                <span className="text-navy-700 font-medium">Tydeligere kommunikation</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-circle-check text-brand-orange text-lg mt-0.5 shrink-0" />
                <span className="text-navy-700 font-medium">Stærkere relationer</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-circle-check text-brand-orange text-lg mt-0.5 shrink-0" />
                <span className="text-navy-700 font-medium">Færre konflikter</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-circle-check text-brand-orange text-lg mt-0.5 shrink-0" />
                <span className="text-navy-700 font-medium">Bedre trivsel</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-circle-check text-brand-orange text-lg mt-0.5 shrink-0" />
                <span className="text-navy-700 font-medium">Højere medarbejderfastholdelse</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-circle-check text-brand-orange text-lg mt-0.5 shrink-0" />
                <span className="text-navy-700 font-medium">Mere følgeskab</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-circle-check text-brand-orange text-lg mt-0.5 shrink-0" />
                <span className="text-navy-700 font-medium">Et lederskab, der holder i længden</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="font-display text-3xl font-bold text-maersk-dark">
            Klar til at tage ansvar for dit lederskab?
          </h2>
          <p className="text-navy-700 max-w-xl mx-auto">
            Bliv en del af næste hold på Tag ansvar for dit lederskab – ud fra dine værdier.
          </p>
          <div className="pt-4">
            <Link
              to="/kommende-hold"
              className="bg-brand-orange hover:bg-brand-orange-hover text-white px-8 py-4 rounded-md font-bold tracking-wide transition-all duration-300 shadow-md inline-block"
            >
              Se kommende hold <i className="fa-solid fa-arrow-right ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
